import {DeleteRecordsConfirmationButton,} from '@flexinet/ui-components';
import {useQueryClient} from '@tanstack/react-query';

import {applicationsKeys, useDeleteApplicationMutation} from '../hooks/useApplicationsQuery';
import {useApplicationsSelection} from "../hooks/useApplicationsSelection.ts";


export const DeleteRecordButton = () => {

    const deleteApplicationMutation = useDeleteApplicationMutation();
    const queryClient = useQueryClient();

    const {
        selection,
        handleClearSelection,
    } = useApplicationsSelection();

    const recordIds = Object.keys(selection).filter(id => selection[id] === true);
    const disabled = recordIds.length === 0;

    // FNP-1028: ONE REJECTION MUST NOT ABANDON THE REST OF THE SELECTION.
    //
    // This used to `await mutateAsync(id)` in a bare loop. FNP-989 makes
    // `deleteApplication` reject on a delete that ACTUALLY HAPPENED, so the
    // first record threw straight out of the loop: every later record was
    // never attempted, and `handleClearSelection()` below never ran — leaving
    // rows checked that no longer exist.
    //
    // From the operator's side the multi-delete "half worked", with nothing
    // saying which half. Each delete is therefore settled on its own; the
    // mutation's own `onError` reports a genuine failure, and its `onSettled`
    // refreshes the list either way.
    //
    // ⚠️ SEQUENTIAL, DELIBERATELY. `Promise.all` would fire N concurrent
    // deletes and N invalidations against a list the operator is watching.
    // These are a handful of records chosen by hand, so the ordering costs
    // nothing and keeps the refetches from stacking up.
    const handleDelete = async () => {
        for (const id of recordIds) {
            try {
                await deleteApplicationMutation.mutateAsync(id);
            } catch {
                // Already surfaced by the mutation's onError, and the list is
                // refreshed by its onSettled. Swallowed here only so the
                // remaining records are still attempted.
            }
        }
        // Reached on every path now. The rows are gone whether or not the
        // response said so, so a selection pointing at them is stale.
        handleClearSelection();

        // ⚠️ ONE FINAL REFETCH, AFTER THE WHOLE LOOP.
        //
        // Each mutation's own `onSettled` invalidates as it finishes, which
        // means the refetch triggered by record N can resolve while record N+1
        // is still in flight — and it then returns a list that still contains
        // N+1. Observed on a 2-record delete in dev: both records were gone
        // from DynamoDB and one row was still on screen.
        //
        // Invalidating once more here, after every delete has settled, is the
        // read that reflects all of them. It is the only invalidation whose
        // timing is guaranteed to be after the last write.
        await queryClient.invalidateQueries({queryKey: applicationsKeys.all});
    };

    return (<DeleteRecordsConfirmationButton
        disabled={disabled}
        recordIdsLength={recordIds.length}
        handleConfirm={handleDelete}
    />);

};
